import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Mail, 
  Plus, 
  Trash2, 
  Play, 
  Pause, 
  Send, 
  FileText, 
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  Settings
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  getScheduledReports,
  createScheduledReport,
  deleteScheduledReport,
  toggleScheduledReport,
  sendScheduledReportNow
} from '../lib/api';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const ScheduledReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    recipient_emails: '',
    frequency: 'weekly',
    day_of_week: 0,
    day_of_month: 1,
    custom_interval_days: 14,
    include_pdf: true,
    include_excel: true,
    include_summary: true,
    report_type: 'roadmap'
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await getScheduledReports();
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.recipient_emails) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const emails = formData.recipient_emails.split(',').map(e => e.trim()).filter(e => e);
      
      await createScheduledReport({
        name: formData.name,
        recipient_emails: emails,
        frequency: formData.frequency,
        day_of_week: formData.frequency === 'weekly' || formData.frequency === 'biweekly' ? formData.day_of_week : null,
        day_of_month: formData.frequency === 'monthly' ? formData.day_of_month : null,
        custom_interval_days: formData.frequency === 'custom' ? formData.custom_interval_days : null,
        include_pdf: formData.include_pdf,
        include_excel: formData.include_excel,
        include_summary: formData.include_summary,
        report_type: formData.report_type,
        roadmap_data: {
          roadmap: [],
          totals: { totalInterventions: 0, totalInvestment: 0, avgLeiIncrease: 0, projectedRoi: 0 },
          goals: { roiPercentile: 75, kpiPercentile: 75, paybackPercentile: 75 }
        }
      });
      
      toast.success('Scheduled report created');
      setShowCreateDialog(false);
      setFormData({
        name: '',
        recipient_emails: '',
        frequency: 'weekly',
        day_of_week: 0,
        day_of_month: 1,
        custom_interval_days: 14,
        include_pdf: true,
        include_excel: true,
        include_summary: true,
        report_type: 'roadmap'
      });
      fetchReports();
    } catch (error) {
      toast.error('Failed to create scheduled report');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (reportId) => {
    try {
      const response = await toggleScheduledReport(reportId);
      toast.success(response.data.message);
      fetchReports();
    } catch (error) {
      toast.error('Failed to toggle report');
    }
  };

  const handleDelete = async (reportId) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) return;
    
    try {
      await deleteScheduledReport(reportId);
      toast.success('Report deleted');
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleSendNow = async (reportId) => {
    setSendingId(reportId);
    try {
      const response = await sendScheduledReportNow(reportId);
      if (response.data.sent_count > 0) {
        toast.success(`Report sent to ${response.data.sent_count} recipient(s)`);
      } else {
        toast.error('Failed to send report');
      }
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingId(null);
    }
  };

  const formatNextSend = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrequencyLabel = (report) => {
    switch (report.frequency) {
      case 'weekly':
        return `Weekly (${DAYS_OF_WEEK.find(d => d.value === report.day_of_week)?.label || 'Monday'})`;
      case 'biweekly':
        return `Every 2 Weeks (${DAYS_OF_WEEK.find(d => d.value === report.day_of_week)?.label || 'Monday'})`;
      case 'monthly':
        return `Monthly (Day ${report.day_of_month || 1})`;
      case 'custom':
        return `Every ${report.custom_interval_days} days`;
      default:
        return report.frequency;
    }
  };

  const __pageTheme = getLightSectionTheme('Insights');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Scheduled Reports`} description={`Automate recurring report deliveries.`} theme={__pageTheme} />
    )}>
    <div className="space-y-6" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700" data-testid="create-schedule-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                Create Scheduled Report
              </DialogTitle>
              <DialogDescription>
                Set up automatic email delivery of your intervention roadmap reports.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Report Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Report Name *</Label>
                <Input
                  id="name"
                  placeholder="Weekly L&D Leadership Report"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="schedule-name-input"
                />
              </div>

              {/* Recipients */}
              <div className="space-y-2">
                <Label htmlFor="emails">Recipient Emails *</Label>
                <Input
                  id="emails"
                  placeholder="clo@company.com, hr@company.com"
                  value={formData.recipient_emails}
                  onChange={(e) => setFormData({ ...formData, recipient_emails: e.target.value })}
                  data-testid="schedule-emails-input"
                />
                <p className="text-xs text-slate-500">Separate multiple emails with commas</p>
              </div>

              {/* Frequency */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select 
                    value={formData.frequency} 
                    onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(formData.frequency === 'weekly' || formData.frequency === 'biweekly') && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select 
                      value={String(formData.day_of_week)} 
                      onValueChange={(v) => setFormData({ ...formData, day_of_week: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(day => (
                          <SelectItem key={day.value} value={String(day.value)}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.frequency === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Day of Month</Label>
                    <Select 
                      value={String(formData.day_of_month)} 
                      onValueChange={(v) => setFormData({ ...formData, day_of_month: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={String(day)}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.frequency === 'custom' && (
                  <div className="space-y-2">
                    <Label>Interval (days)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={90}
                      value={formData.custom_interval_days}
                      onChange={(e) => setFormData({ ...formData, custom_interval_days: parseInt(e.target.value) || 14 })}
                    />
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div className="space-y-3">
                <Label>Include in Email</Label>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="pdf"
                      checked={formData.include_pdf}
                      onCheckedChange={(v) => setFormData({ ...formData, include_pdf: v })}
                    />
                    <label htmlFor="pdf" className="text-sm flex items-center gap-1 cursor-pointer">
                      <FileText className="h-4 w-4 text-rose-500" />
                      PDF
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="excel"
                      checked={formData.include_excel}
                      onCheckedChange={(v) => setFormData({ ...formData, include_excel: v })}
                    />
                    <label htmlFor="excel" className="text-sm flex items-center gap-1 cursor-pointer">
                      <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                      Excel
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="summary"
                      checked={formData.include_summary}
                      onCheckedChange={(v) => setFormData({ ...formData, include_summary: v })}
                    />
                    <label htmlFor="summary" className="text-sm cursor-pointer">
                      Summary in Email
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={creating}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Schedule
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No Scheduled Reports</h3>
            <p className="text-slate-500 mt-1 mb-4">
              Create a schedule to automatically send roadmap reports to stakeholders.
            </p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Schedule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reports.map((report) => (
            <Card key={report.id} className={!report.is_active ? 'opacity-60' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-800">{report.name}</h3>
                      <Badge variant={report.is_active ? 'default' : 'secondary'}>
                        {report.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Recipients:</span>
                        <p className="text-slate-700 truncate">
                          {report.recipient_emails?.join(', ') || 'None'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Frequency:</span>
                        <p className="text-slate-700">{getFrequencyLabel(report)}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Next Send:</span>
                        <p className="text-slate-700">{formatNextSend(report.next_send_at)}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Sent:</span>
                        <p className="text-slate-700">{report.send_count || 0} times</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                      {report.include_pdf && (
                        <Badge variant="outline" className="text-xs">
                          <FileText className="h-3 w-3 mr-1 text-rose-500" />
                          PDF
                        </Badge>
                      )}
                      {report.include_excel && (
                        <Badge variant="outline" className="text-xs">
                          <FileSpreadsheet className="h-3 w-3 mr-1 text-emerald-500" />
                          Excel
                        </Badge>
                      )}
                      {report.include_summary && (
                        <Badge variant="outline" className="text-xs">
                          Summary
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendNow(report.id)}
                      disabled={sendingId === report.id}
                      data-testid={`send-now-${report.id}`}
                    >
                      {sendingId === report.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggle(report.id)}
                    >
                      {report.is_active ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(report.id)}
                      className="text-rose-500 hover:text-rose-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-slate-400 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-1">About Scheduled Reports</p>
              <ul className="list-disc list-inside space-y-1 text-slate-500">
                <li>Reports are sent at 9:00 AM UTC on scheduled days</li>
                <li>Configure your roadmap data in the analysis pages before scheduling</li>
                <li>Use "Send Now" to test email delivery to recipients</li>
                <li>Paused reports will not send until reactivated</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageShell>
  );
};

export default ScheduledReportsPage;
